import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideBook,
  lucideChevronRight,
  lucideChevronDown,
  lucideArrowLeft,
} from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { SidebarService } from '@shared/services/sidebar.service';
import { HelpService } from '@features/help/services/help.service';
import { HelpDocSummary } from '@features/help/models/help.model';

export interface DocsSidebarItem {
  label: string;
  icon: string;
  route?: string;
  children?: DocsSidebarItem[];
  expanded?: boolean;
}

export interface DocsSidebarSection {
  title: string;
  items: DocsSidebarItem[];
}

@Component({
  selector: 'app-docs-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, NgIconComponent, HlmButtonDirective],
  providers: [
    provideIcons({
      lucideBook,
      lucideChevronRight,
      lucideChevronDown,
      lucideArrowLeft,
    }),
  ],
  templateUrl: './docs-sidebar.html',
  styleUrl: './docs-sidebar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocsSidebarComponent {
  readonly sidebarService = inject(SidebarService);
  private router = inject(Router);
  private helpService = inject(HelpService);

  readonly isCollapsed = this.sidebarService.isCollapsed;

  docsSummary = signal<HelpDocSummary[]>([]);

  // Grouped docs by category
  groupedDocs = computed(() => {
    const docs = this.docsSummary();
    const grouped = docs.reduce((acc, doc) => {
      if (!acc[doc.category]) {
        acc[doc.category] = [];
      }
      acc[doc.category].push(doc);
      return acc;
    }, {} as Record<string, HelpDocSummary[]>);

    // Sort within categories by order
    Object.keys(grouped).forEach((category) => {
      grouped[category].sort((a, b) => a.order - b.order);
    });

    return grouped;
  });

  constructor() {
    this.loadDocsSummary();
  }

  async loadDocsSummary(): Promise<void> {
    try {
      this.helpService.getDocsSummary().subscribe({
        next: (docs) => {
          this.docsSummary.set(docs);
        },
        error: (error) => {
          console.error('Error loading docs:', error);
        },
      });
    } catch (error) {
      console.error('Error loading docs:', error);
    }
  }

  goToApp(): void {
    this.router.navigate(['/']);
  }

  toggleCollapse(): void {
    this.sidebarService.toggleSidebar();
  }
}
